import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getProfiles,
  getMyProfile,
  uploadAvatar,
  updateFplId,
  changePassword,
  updateProfileSettings,
  updateProfileUiFlags,
} from '@/api/profiles';
import { ACCESS_TOKEN_KEY, MY_PROFILE_QUERY_KEY } from '@/constants';
import { notify } from '@/services/notifications';
import type { ProfileDto } from '@/types/profiles';
export function useProfileByUsername(username: string) {
  return useQuery<{ profile: ProfileDto | null }>({
    queryKey: ['profiles', 'by-username', username],
    enabled: !!username,
    queryFn: async () => {
      const res = await getProfiles({ username, limit: 1, offset: 0 });
      return { profile: res.profiles[0] ?? null };
    },
    staleTime: 60 * 1000,
  });
}

// Get My Profile (shares cache with useMe - single /me request)
export function useMyProfile() {
  const hasAccessToken = typeof window !== 'undefined' && !!localStorage.getItem(ACCESS_TOKEN_KEY);
  return useQuery({
    queryKey: MY_PROFILE_QUERY_KEY,
    queryFn: async () => {
      const res = await getMyProfile();
      return res.data;
    },
    enabled: hasAccessToken,
    staleTime: 30 * 1000,
  });
}

// Upload Avatar
export function useUploadAvatar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: uploadAvatar,
    onSuccess: (res) => {
      notify.success('Avatar uploaded successfully');
      queryClient.invalidateQueries({ queryKey: MY_PROFILE_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
      // Update cached profile data
      queryClient.setQueryData(MY_PROFILE_QUERY_KEY, res.data);
    },
    onError: (err: unknown) => {
      const e = err as { status?: number; code?: string; message?: string };
      if (e.status === 429) {
        notify.error('Too many uploads. Please wait a minute.');
      } else {
        notify.error(e.message || 'Failed to upload avatar');
      }
    },
  });
}

// Update FPL ID
export function useUpdateFplId(options?: { silent?: boolean }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateFplId,
    onSuccess: (res) => {
      if (!options?.silent) {
        notify.success('FPL ID updated successfully');
      }
      queryClient.invalidateQueries({ queryKey: MY_PROFILE_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
      // Update cached profile data
      queryClient.setQueryData(MY_PROFILE_QUERY_KEY, res.data);
    },
    onError: (err: unknown) => {
      const e = err as { status?: number; code?: string; message?: string };
      if (!options?.silent) {
        notify.error(e.message || 'Failed to update FPL ID');
      }
    },
  });
}

// Change Password
export function useChangePassword() {
  return useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      notify.success('Password changed successfully');
    },
    onError: (err: unknown) => {
      const e = err as { status?: number; code?: string; message?: string };
      if (e.status === 401) {
        notify.error('Current password is incorrect');
      } else {
        notify.error(e.message || 'Failed to change password');
      }
    },
  });
}

// Update Profile Settings
export function useUpdateProfileSettings(options?: { silent?: boolean }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateProfileSettings,
    onSuccess: (res) => {
      // Don't show generic notification - let caller show specific message
      queryClient.invalidateQueries({ queryKey: MY_PROFILE_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
      void queryClient.invalidateQueries({ queryKey: ['leagues', 'me'] });
      // Update cached profile data
      queryClient.setQueryData(MY_PROFILE_QUERY_KEY, res.data);
    },
    onError: (err: unknown) => {
      const e = err as { status?: number; code?: string; message?: string };
      if (!options?.silent) {
        notify.error(e.message || 'Failed to update settings');
      }
    },
  });
}

// Update UI flags (e.g. follow_x_modal_shown)
export function useUpdateProfileUiFlags() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateProfileUiFlags,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: MY_PROFILE_QUERY_KEY });
      queryClient.setQueryData(MY_PROFILE_QUERY_KEY, res.data);
    },
  });
}

